import {Inject, Injectable} from '@angular/core';
import {MsalBroadcastService, MsalService} from "@azure/msal-angular";
import {BehaviorSubject, filter, Subject, takeUntil} from "rxjs";
import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  InteractionStatus,
  SilentRequest,
} from '@azure/msal-browser';
import {MixpanelService} from "./mixpanel.service";
import { API_RESOURCE, MSALAcquireTokenRedirect, MSALAcquireTokenSilent, MSALDoesAuthenticationFailureRequireRedirect, ProtectedResource } from 'src/app/config/MsalLoader.service';

export enum AuthenticationEvent {
  Authenticated,
  ImpersonationStarted,
  ImpersonationStopped
}

export class AuthenticationEventEmitter {
  private events: Record<AuthenticationEvent, Function[]> = {
    [AuthenticationEvent.Authenticated]: [],
    [AuthenticationEvent.ImpersonationStarted]: [],
    [AuthenticationEvent.ImpersonationStopped]: []
  };

  subscribe(event: AuthenticationEvent, callback: Function): void {
    this.events[event].push(callback);
  }

  trigger(event: AuthenticationEvent, data?: any): void {
    this.events[event].forEach(callback => callback(data));
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  controller = new AbortController();
  private readonly _destroying$ = new Subject();
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();


  constructor(private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private mixPanelService: MixpanelService,
    @Inject(API_RESOURCE) private apiResource: ProtectedResource,
  ) {
  }

  public readonly events = new AuthenticationEventEmitter();

  public listenForAndHandleInProgressLogin() {
    this.msalBroadcastService.inProgress$
      .pipe(
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.msalService.handleRedirectObservable().subscribe(result => {
          if (this.msalService.instance.getAllAccounts().length > 0){
            this.setAuthenticated(this.msalService.instance.getAllAccounts().length > 0);
            this.msalService.instance.setActiveAccount(this.msalService.instance.getAllAccounts()[0]);
            this.events.trigger(AuthenticationEvent.Authenticated);
            this.mixPanelService.identify();
          }
        });
      });
  }

  public forceAuthenticationIfUsernameInactive() {
    if (!this.isUserActive()) {
      localStorage.clear();
    }
  }

  public isUserActive() {
    return this.msalService.instance.getActiveAccount() != null;
  }

  public async getAccessToken(): Promise<string | void> {
    try {
      const accessToken = await MSALAcquireTokenSilent(this.msalService, this.apiResource.scopes);
      return accessToken;
    } catch (error) {
      if (MSALDoesAuthenticationFailureRequireRedirect(error)) {
        // If silent call fails, we need to redirect to login page
        // fallback to interaction when silent call fails
        MSALAcquireTokenRedirect(this.msalService);
      }
      else {
        console.error('Error acquiring token silently:', error);
      }
    }
  }

  public async refreshAccessToken() {
    const request: SilentRequest = {
      scopes: this.apiResource.scopes
    };

    try {
      await MSALAcquireTokenSilent(this.msalService, this.apiResource.scopes);
    } catch (error) {
      if (MSALDoesAuthenticationFailureRequireRedirect(error)) {
        // fallback to interaction when silent call fails
        MSALAcquireTokenRedirect(this.msalService);
      }
      else {
        console.error('Error refreshing access token:', error);
      }
    }
  }

  public startImpersonation(username: string) {
    localStorage.setItem('onBehalfOf', username);
    this.events.trigger(AuthenticationEvent.ImpersonationStarted);
  }

  public stopImpersonation() {
    localStorage.removeItem('customerName');
    localStorage.removeItem('userName');
    localStorage.removeItem('onBehalfOf');
    this.events.trigger(AuthenticationEvent.ImpersonationStopped);
  }

  public get isImpersonating(): boolean {
    return this.onBehalfOf != undefined && this.onBehalfOf != null && this.onBehalfOf != '';
  }

  public get onBehalfOf() {
    return localStorage['onBehalfOf'];
  }

  public get customerName() {
    return localStorage['customerName'];
  }

  public get userName() {
    return localStorage['UserName'];
  }

  setAuthenticated(isAuthenticated: boolean) {
    this.isAuthenticatedSubject.next(isAuthenticated);
  }
  
  logout(){
    this.msalService.logout();
  }
}
